import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ConfigurationService } from '@app/app-initialisers/configuration-service/configuration.service';
import { Order } from '@app/models/Order';
import { PaymentSessionCreate } from '@app/models/payment-session-create';
import { PaymentSession } from '@app/models/payment-session';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  public currentOrder: BehaviorSubject<Order>;

  private deletedOrders: { [id: string]: 'started' | 'completed' } = {};

  constructor(
    private http: HttpClient,
    public configurationService: ConfigurationService,
  ) {
    this.currentOrder = new BehaviorSubject<Order>(null);
  }

  /**
   * does an HTTP POST/order to the API
   * @param order
   */
  public createOrder(order: Order): Observable<Order> {
    return this.http.post<Order>(`${this.configurationService.getApiBaseUrlForTenant()}/orders`, order)
        .pipe(map((data: Order) => {
          this.currentOrder.next(data);
          return data;
        }));
  }

  /**
   * does an HTTP Get/orders/{orderId} and returns the order
   * @param orderId
   */
  public getOrderAsCurrentFromId(orderId: string): Observable<Order> {
    return this.http.get<Order>(`${this.configurationService.getApiBaseUrlForTenant()}/orders/${orderId}`)
        .pipe(map((data: Order) => {
          this.currentOrder.next(data);
          return data;
        }));
  }

  /**
   * does an HTTP/delete to delete the order with matching Id
   * @param orderId
   */
  public deleteOrderById(orderId: string): Promise<null> {
    if (this.deletedOrders[orderId]) {
      return this.deletedOrders[orderId] === 'completed' ?
        Promise.resolve(null) :
        new Promise((resolve) => this.currentOrder.asObservable().pipe(take(1)).subscribe(() => resolve(null)));
    }

    this.deletedOrders[orderId] = 'started';

    return this.http.delete<Order>(`${this.configurationService.getApiBaseUrlForTenant()}/orders/${orderId}`)
        .toPromise()
        .then((order: Order) => {
          this.deletedOrders[orderId] = 'completed';
          this.currentOrder.next(order);
          return null;
        });
  }

  /**
   * does an HTTP POST/payments to create a payment session
   * @param payment
   */
  public createPayment(payment: PaymentSessionCreate): Observable<PaymentSession> {
    return this.http.post<PaymentSession>(`${this.configurationService.getApiBaseUrlForTenant()}/payments`, payment)
        .pipe(map((data: PaymentSession) => data));
  }
}

